import { inferTemplateFileCategories } from "@/lib/utils/inferTemplateCategories";
import toast from "react-hot-toast";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  MultiselectFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useCreateMatterTemplate } from "@/lib/data/react-query/matters/matter-template";
import { acceptedFileMimeTypesDocumentOnly } from "@/lib/data/static/fileStatics";
import { MATTER_TEMPLATE_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { getFileNameParts } from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Upload, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface BulkUploadMatterTemplatesModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const MAX_BULK_UPLOAD_COUNT = 50;

const limitMessage = `You can upload up to ${MAX_BULK_UPLOAD_COUNT} templates at a time`;

const fileSchema =
  typeof File === "undefined" ? z.custom<File>() : z.instanceof(File);

const bulkUploadFormSchema = z.object({
  category: z.array(z.enum(MATTER_TEMPLATE_CATEGORY_OPTIONS)).optional(),
  files: z
    .array(fileSchema)
    .min(1, { message: "At least one file is required" })
    .max(MAX_BULK_UPLOAD_COUNT, { message: limitMessage }),
});

const defaultValues: z.infer<typeof bulkUploadFormSchema> = {
  category: [],
  files: [],
};

const FORM_ID = "bulk-upload-matter-templates-form";

export function BulkUploadMatterTemplatesModal({
  open,
  setOpen,
}: BulkUploadMatterTemplatesModalProps) {
  const [uploadStatuses, setUploadStatuses] = useState<
    Map<File, "uploading" | "uploaded" | "failed">
  >(new Map());
  const setUploadStatus = (
    file: File,
    status: "uploading" | "uploaded" | "failed",
  ) => {
    setUploadStatuses((previous) => new Map(previous).set(file, status));
  };
  const uploadAttempts = useRef(
    new Map<
      File,
      {
        operationId: string;
        key?: string;
        categories?: (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number][];
      }
    >(),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createMatterTemplate = useCreateMatterTemplate({ showToast: false });

  const form = useForm<z.infer<typeof bulkUploadFormSchema>>({
    resolver: zodResolver(bulkUploadFormSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const isFormLoading =
    form.formState.isSubmitting || createMatterTemplate.isPending;

  const selectedFiles = form.watch("files", []);

  useEffect(() => {
    if (!open) {
      setUploadStatuses(new Map());
      uploadAttempts.current.clear();
      form.reset(defaultValues);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open, form]);

  const handleFileSelection = (
    files: FileList | null,
    onChange: (value: File[]) => void,
    currentFiles: File[],
  ) => {
    if (!files) return;
    const incomingFiles = Array.from(files);
    const availableSlots = MAX_BULK_UPLOAD_COUNT - currentFiles.length;

    if (availableSlots <= 0) {
      form.setError("files", {
        type: "manual",
        message: limitMessage,
      });
      return;
    }

    if (incomingFiles.length > availableSlots) {
      form.setError("files", {
        type: "manual",
        message: limitMessage,
      });
    } else {
      form.clearErrors("files");
    }

    const nextFiles = currentFiles.concat(
      incomingFiles.slice(0, availableSlots),
    );
    onChange(nextFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, idx) => idx !== index);
    form.setValue("files", updatedFiles, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (updatedFiles.length < MAX_BULK_UPLOAD_COUNT) {
      form.clearErrors("files");
    }
  };

  const onSubmit = async (data: z.infer<typeof bulkUploadFormSchema>) => {
    const categories = data.category ?? [];

    let failedCount = 0;
    for (const file of data.files) {
      if (uploadStatuses.get(file) === "uploaded") continue;
      const attempt = uploadAttempts.current.get(file) ?? {
        operationId: crypto.randomUUID(),
      };
      uploadAttempts.current.set(file, attempt);
      setUploadStatus(file, "uploading");
      try {
        const { fileName, fileExtension } = getFileNameParts(file.name);
        const name = fileExtension ? `${fileName}.${fileExtension}` : fileName;
        let fileCategories = attempt.categories ?? categories;
        if (!attempt.categories && categories.length === 0) {
          try {
            fileCategories = await inferTemplateFileCategories(file);
          } catch {
            toast.error(
              `Could not detect categories for ${file.name}. Uploading without categories.`,
            );
          }
        }
        attempt.categories = fileCategories;
        await createMatterTemplate.mutateAsync({
          operationId: attempt.operationId,
          presignedUrlKey: attempt.key,
          type: fileExtension,
          onSourceUploaded: (key) => {
            attempt.key = key;
          },
          category: fileCategories,
          name,
          file,
        });
        setUploadStatus(file, "uploaded");
      } catch {
        failedCount += 1;
        setUploadStatus(file, "failed");
      }
    }
    if (failedCount === 0) {
      toast.success("All templates uploaded");
      setOpen(false);
    } else {
      toast.error(
        `${failedCount} template uploads failed. Retry the failed uploads.`,
      );
    }
  };

  const closeModal = (nextOpen: boolean) => {
    if (!isFormLoading) setOpen(nextOpen);
  };

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={closeModal}
        title="Bulk upload templates"
        description="Upload up to 50 templates at a time. Selected categories apply to each template. Leave categories empty to detect them from each DOCX."
        isDirty={form.formState.isDirty}
        loading={isFormLoading}
        size="xl"
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton
              setOpen={closeModal}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              submitFormId={FORM_ID}
              title={
                uploadStatuses.size > 0
                  ? "Retry failed uploads"
                  : "Upload templates"
              }
              loading={isFormLoading}
            />
          </div>
        }
      >
        <form
          id={FORM_ID}
          className="flex min-h-0 grow flex-col overflow-hidden"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="grow overflow-y-auto">
            <fieldset className={slideoverFieldsetClassName}>
              <div className="col-span-2">
                <MultiselectFormField
                  label="Categories"
                  options={MATTER_TEMPLATE_CATEGORY_OPTIONS.map((option) => ({
                    label: option,
                    value: option,
                  }))}
                  placeholder="Select categories"
                  maxCount={5}
                  register={form.register("category")}
                />
              </div>
              <FormField
                control={form.control}
                name="files"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <div className="flex items-center justify-between gap-2">
                      <FormLabel>Files*</FormLabel>
                      <Typography className="text-muted-foreground text-xs">
                        {limitMessage}
                      </Typography>
                    </div>
                    <FormControl>
                      <>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          disabled={isFormLoading || uploadStatuses.size > 0}
                          onClick={() => {
                            fileInputRef.current?.click();
                          }}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          Select files
                        </Button>
                        <input
                          type="file"
                          multiple
                          ref={fileInputRef}
                          className="hidden"
                          onChange={(e) => {
                            handleFileSelection(
                              e.target.files,
                              field.onChange,
                              field.value,
                            );
                          }}
                          accept={acceptedFileMimeTypesDocumentOnly.join(",")}
                        />
                      </>
                    </FormControl>
                    <FormMessage />
                    {uploadStatuses.size > 0 && (
                      <div role="status">
                        {
                          Array.from(uploadStatuses.values()).filter(
                            (status) => status === "uploaded",
                          ).length
                        }{" "}
                        of {selectedFiles.length} uploaded
                      </div>
                    )}
                    {selectedFiles.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {selectedFiles.map((selectedFile, index) => (
                          <div
                            key={`${selectedFile.name}-${index}`}
                            className="flex items-center justify-between rounded border border-dashed px-3 py-2 text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4" />
                              <div className="max-w-xs truncate">
                                <Typography className="truncate font-medium">
                                  {selectedFile.name}
                                </Typography>
                                <div role="status">
                                  {uploadStatuses.get(selectedFile) ??
                                    "Waiting"}
                                </div>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={
                                isFormLoading ||
                                uploadStatuses.has(selectedFile)
                              }
                              onClick={() => {
                                handleRemoveFile(index);
                              }}
                            >
                              <XCircle className="mr-1 h-4 w-4" />
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </FormItem>
                )}
              />
            </fieldset>
          </div>
        </form>
      </AppModal>
    </Form>
  );
}
